import Component from '@theme/components/Component';
import { fetchConfig } from '@theme/utils/fetch-config';
import { CartAddEvent, CartErrorEvent } from '@theme/events/cart';
import { cartPerformance } from '@theme/performance/cart-performance';

class AddToCartComponent extends Component {
  #timeout = null;

  get addToCartButton() {
    return this.refs.addToCartButton;
  }

  setLoading(isLoading) {
    if (!this.addToCartButton) return;

    if (isLoading) {
      this.addToCartButton.setAttribute('aria-busy', 'true');
      this.addToCartButton.classList.add('is-loading');
    } else {
      this.addToCartButton.removeAttribute('aria-busy');
      this.addToCartButton.classList.remove('is-loading');
    }
  }
}

customElements.define('add-to-cart-component', AddToCartComponent);

class ProductFormComponent extends Component {
  #timeout = null;

  connectedCallback() {
    super.connectedCallback();

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    document.addEventListener('variant:change', (event) => {
      const variant = event?.detail?.variant;
      if (!variant) return;

      const variantInput = this.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }
    });
  }

  #setLiveRegionText(message) {
    const liveRegion = this.refs?.liveRegion;
    if (!liveRegion) return;
    liveRegion.textContent = message;
    liveRegion.setAttribute('aria-live', 'polite');
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs?.liveRegion;
    if (!liveRegion) return;
    liveRegion.textContent = '';
    liveRegion.removeAttribute('aria-live');
  }

  handleSubmit(event) {
    const { addToCartTextError } = this.refs;

    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') ===
      'true'
    ) {
      return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              response.message,
              response.description,
              response.errors,
            ),
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount:
                Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );

          return;
        }

        const id = formData.get('id');

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        if (!id) throw new Error('Form ID is required');

        if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
          const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
          const addedTextElement =
            addToCartButton.querySelector('.add-to-cart-text--added');
          const addedText =
            addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, 5000);
        }

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: 'product-form-component',
            itemCount:
              Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
            sections: response.sections,
          }),
        );
      })
      .catch(() => {
        // Silently handle fetch errors (e.g. aborted requests)
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }
}

customElements.define('product-form-component', ProductFormComponent);
